import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DndContext, DragOverlay, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import IconAdd from '../../components/icons/IconAdd';
import TopicElementCard from './TopicElementCard';
import TopicContainerCard from './TopicContainerCard';
import TopicExerciseCard from './TopicExerciseCard';
import ExerciseTemplatePicker from './ExerciseTemplatePicker';
import ExerciseTemplateCreateModal from '../exercise-templates/ExerciseTemplateCreateModal';
import DroppableRegion from '../../components/dnd/DroppableRegion';
import { TOPIC_BLOCK_TYPE_LIST, getTopicBlockType } from './topicBlockTypes';
import { CONTAINER_LAYOUT_TYPE_LIST } from '../../variables/containerLayoutTypes';
import { mergeTopicContent, nextTopicContentPosition, computeTopicContentMove, containerIdOf, TOPIC_ENTRY_KIND_BLOCK, TOPIC_ENTRY_KIND_CONTAINER, TOPIC_ENTRY_KIND_EXERCISE } from './topicContent';
import { buildTopicElementPayload, buildTopicContainerPayload, buildTopicExerciseTemplatePayload } from './coursesPayload';
import { makeDndId, TOP_LEVEL_END_ID } from '../../components/dnd/dndId';
import { applyDragPreview, buildDndIndex, findEntryInScopes, resolveOver } from '../../components/dnd/containerDragPreview';
import { createContainerCollisionDetection } from '../../components/dnd/collisionDetection';
import * as api from './api';

const TOPIC_ELEMENT_BASE = '/api/topicelements';
const TOPIC_CONTAINER_BASE = '/api/topiccontainers';
const TOPIC_EXERCISE_BASE = '/api/topicexercisetemplates';

function dndIdOfEntry(entry) {
   if (entry.kind === TOPIC_ENTRY_KIND_BLOCK) return makeDndId('block', entry.item.id);
   if (entry.kind === TOPIC_ENTRY_KIND_CONTAINER) return makeDndId('container', entry.item.id);
   return makeDndId('exerciseLink', entry.item.id);
}

// A Topic's own top-level content: TopicElement blocks, TopicContainer
// layout regions, and TopicExerciseTemplate links, all interleaved into one
// ordered sequence (see topicContent.js) - exercise-links used to be a
// separate, always-at-the-bottom panel (LinkedExercisesPanel.jsx, now
// removed); they're now just another top-level entry kind, addable from
// this same picker and reorderable/draggable right alongside blocks and
// containers, so a topic can be filled in any order (content, then an
// exercise, then more content below it, etc).
//
// Drag-and-drop: any block, wherever it currently lives (top-level or
// inside any container), can be dragged into any container or back out to
// top-level. Containers themselves stay non-draggable (they never nest) -
// see TopicContainerCard's `useSortable({disabled:true})` - but still
// occupy their slot in this top-level SortableContext so neighbor-reflow
// math around them stays correct. Exercise-links are draggable for
// top-level reordering only - never containerizable (TopicExerciseTemplate
// has no `container` FK on the backend at all) - handleDragOver below
// explicitly ignores any resolution that would drop one into a container.
// Canonical `elements`/`containers`/`exerciseLinks` are NEVER mutated
// during a drag - `dragPreview` is a pure render-time overlay (see
// containerDragPreview.js), so "drag out without dropping" needs zero
// rollback bookkeeping, just clearing that one piece of state.
export default function TopicElementList({ topicId, courseId, refreshSignal }) {
   const navigate = useNavigate();
   const [elements, setElements] = useState([]);
   const [containers, setContainers] = useState([]);
   const [exerciseLinks, setExerciseLinks] = useState([]);
   const [refresh, setRefresh] = useState(0);
   const [pickerOpen, setPickerOpen] = useState(false);
   const [pickedExerciseId, setPickedExerciseId] = useState('');
   const [createExerciseOpen, setCreateExerciseOpen] = useState(false);
   const [dragPreview, setDragPreview] = useState(null);
   const [awaitingCommit, setAwaitingCommit] = useState(false);
   const lastOverIdRef = useRef(null);
   const collisionDetectionRef = useRef(createContainerCollisionDetection(lastOverIdRef));
   // Always in sync with the pointer (updated synchronously on every
   // onDragOver, never debounced) - handleDragEnd commits from THIS, not
   // from the (debounced) dragPreview state below, so a drop always
   // reflects the truly-latest resolved position even if the visible
   // preview hasn't caught up to it yet.
   const latestOverRef = useRef(null);
   const dragOverDebounceRef = useRef(null);
   // Hysteresis for a real, structural feedback loop: showing the ACTIVE
   // item's own placeholder inside its ORIGIN container (the default at
   // drag-start, so the item doesn't visually yank away immediately) can
   // shift that container's OTHER children enough to cross back over a
   // now-stationary pointer, which re-resolves as "hovering a child",
   // re-inserting the placeholder, re-growing the container, moving the
   // children again - oscillating indefinitely rather than settling,
   // because leaving and returning both look like equally valid resolved
   // positions. Leaving the origin is always trusted instantly (that's
   // real, intentional movement); once a drag has left, a resolution that
   // points back INTO the origin is treated with suspicion instead of
   // applied immediately - only committed if it's still the answer after a
   // short delay, so a single noisy frame caused by the origin's own
   // reflow can't re-trigger the loop.
   const originContainerRef = useRef(null);
   // Captured once at drag-start (an entry's kind never changes mid-drag) -
   // lets handleDragOver reject a resolution without depending on the
   // (debounced, so potentially a render behind) dragPreview state.
   const activeKindRef = useRef(null);
   const hasLeftOriginRef = useRef(false);
   const returnToOriginDebounceRef = useRef(null);
   const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
   const reload = () => setRefresh((prev) => prev + 1);

   useEffect(() => {
      api.getList(TOPIC_ELEMENT_BASE, 'topic', topicId)
         .then((response) => setElements(Array.isArray(response.data) ? response.data : []))
         .catch(() => setElements([]));
      api.getList(TOPIC_CONTAINER_BASE, 'topic', topicId)
         .then((response) => setContainers(Array.isArray(response.data) ? response.data : []))
         .catch(() => setContainers([]));
      api.getTopicExerciseTemplates(topicId)
         .then((response) => setExerciseLinks(Array.isArray(response.data) ? response.data : []))
         .catch(() => setExerciseLinks([]));
   }, [topicId, refresh, refreshSignal]);

   // Clears the drag preview only once the canonical data it predicted has
   // actually landed - clearing it the instant the commit PUTs resolve
   // would show a visible snap-back-then-forward flash during the refetch's
   // own network round-trip (old order -> briefly reverted -> new order).
   useEffect(() => {
      if (awaitingCommit) {
         setDragPreview(null);
         setAwaitingCommit(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [elements, containers, exerciseLinks]);

   const topLevel = mergeTopicContent({ elements, containers, exerciseLinks, containerId: null });
   const topLevelEntries = topLevel.map((entry) => ({ ...entry, dndId: dndIdOfEntry(entry) }));

   const allScopes = [{ containerId: null, entries: topLevelEntries }];
   containers.forEach((container) => {
      const children = mergeTopicContent({ elements, containers: [], containerId: container.id })
         .map((entry) => ({ ...entry, dndId: makeDndId('block', entry.item.id) }));
      allScopes.push({ containerId: container.id, entries: children });
   });
   const dndIndex = buildDndIndex(allScopes);

   const renderedTopLevel = applyDragPreview(topLevelEntries, dragPreview, null);

   // Shared per-kind save dispatch - used by both the up/down move buttons
   // and the drag-and-drop commit, so the two stay in lockstep instead of
   // duplicating this 3-way branch twice.
   const saveEntry = (entry, changes = {}) => {
      if (entry.kind === TOPIC_ENTRY_KIND_BLOCK) {
         return api.updateOne(TOPIC_ELEMENT_BASE, entry.item.id, buildTopicElementPayload(topicId, entry.item, changes));
      }
      if (entry.kind === TOPIC_ENTRY_KIND_CONTAINER) {
         return api.updateOne(TOPIC_CONTAINER_BASE, entry.item.id, buildTopicContainerPayload(topicId, entry.item, changes));
      }
      return api.updateOne(TOPIC_EXERCISE_BASE, entry.item.id, buildTopicExerciseTemplatePayload(topicId, entry.item, changes));
   };

   const handleAddBlock = (blockType) => {
      setPickerOpen(false);
      const position = nextTopicContentPosition(topLevel);
      const draft = { type: blockType.type, config: JSON.stringify(blockType.defaultConfig), position };
      api.createOne(TOPIC_ELEMENT_BASE, buildTopicElementPayload(topicId, draft))
         .then(() => reload())
         .catch((err) => console.error(err));
   };

   const handleAddContainer = (layoutType) => {
      setPickerOpen(false);
      const position = nextTopicContentPosition(topLevel);
      const draft = { layout: layoutType.key, config: JSON.stringify(layoutType.defaultConfig), position };
      api.createOne(TOPIC_CONTAINER_BASE, buildTopicContainerPayload(topicId, draft))
         .then(() => reload())
         .catch((err) => console.error(err));
   };

   // Attach an EXISTING exercise template - same idempotent-on-the-backend
   // attach as the old LinkedExercisesPanel.jsx, just now positioned into
   // the shared top-level sequence instead of its own separate one.
   const handleAttachExercise = () => {
      if (!pickedExerciseId) return;
      setPickerOpen(false);
      const position = nextTopicContentPosition(topLevel);
      api.createOne(TOPIC_EXERCISE_BASE, { topic: String(topicId), exerciseTemplate: String(pickedExerciseId), position })
         .then(() => { setPickedExerciseId(''); reload(); })
         .catch((err) => console.error(err));
   };

   // "Create new" bypasses the picker entirely: make the ExerciseTemplate,
   // link it to this topic (position = next slot in the SHARED sequence),
   // then jump straight into its Builder - already linked, with a returnTo
   // back to this exact course.
   const handleExerciseCreated = (newTemplate) => {
      setCreateExerciseOpen(false);
      setPickerOpen(false);
      const position = nextTopicContentPosition(topLevel);
      api.createOne(TOPIC_EXERCISE_BASE, { topic: String(topicId), exerciseTemplate: String(newTemplate.id), position })
         .then(() => navigate('/exercise-templates/' + newTemplate.id, { state: { returnTo: '/courses/' + courseId } }))
         .catch((err) => console.error(err));
   };

   const handleMove = (index, direction) => {
      const swap = computeTopicContentMove(topLevel, index, direction);
      if (!swap) return;
      Promise.all(swap.map((entry) => saveEntry(entry))).then(() => reload()).catch((err) => console.error(err));
   };

   const handleDragStart = (event) => {
      const activeId = event.active.id;
      const activeEntry = findEntryInScopes(allScopes, activeId);
      if (!activeEntry) return;
      const originContainerId = activeEntry.kind === TOPIC_ENTRY_KIND_BLOCK ? containerIdOf(activeEntry.item) : null;
      const originScope = allScopes.find((scope) => scope.containerId === originContainerId);
      const originIndex = originScope ? originScope.entries.findIndex((entry) => entry.dndId === activeId) : 0;
      originContainerRef.current = originContainerId;
      activeKindRef.current = activeEntry.kind;
      hasLeftOriginRef.current = false;
      latestOverRef.current = { containerId: originContainerId, index: originIndex };
      setDragPreview({ activeId, activeEntry, overContainerId: originContainerId, overIndex: originIndex });
   };

   // Applies a CONFIRMED resolution: `latestOverRef` updates synchronously
   // (so handleDragEnd's commit is never stale, even if the visible preview
   // below hasn't caught up yet), while the visible `dragPreview` state
   // itself is debounced by 120ms - collision detection re-measures against
   // whatever just rendered, so updating the state on every single event
   // (rather than letting rapid-fire events collapse into one) can itself
   // become a re-render/re-measure feedback loop. Callers decide whether a
   // resolution is trustworthy enough to call this at all - see
   // `handleDragOver`'s origin-hysteresis handling below for why "resolved
   // back into the item's own origin container" needs extra scrutiny
   // before ever reaching here.
   const commitResolved = (active, resolved) => {
      latestOverRef.current = resolved;
      clearTimeout(dragOverDebounceRef.current);
      dragOverDebounceRef.current = setTimeout(() => {
         setDragPreview((prev) => (prev && prev.activeId === active.id ? { ...prev, overContainerId: resolved.containerId, overIndex: resolved.index } : prev));
      }, 120);
   };

   const handleDragOver = (event) => {
      const { active, over } = event;
      if (!over) return;
      const resolved = resolveOver(over.id, dndIndex, active.id, allScopes);
      if (!resolved) return;

      // Exercise-links have no `container` FK on the backend at all - never
      // let one resolve "into" a container mid-drag.
      if (activeKindRef.current === TOPIC_ENTRY_KIND_EXERCISE && resolved.containerId !== null) return;

      const isOrigin = resolved.containerId === originContainerRef.current;
      if (!isOrigin) {
         hasLeftOriginRef.current = true;
         clearTimeout(returnToOriginDebounceRef.current);
         commitResolved(active, resolved);
         return;
      }
      if (!hasLeftOriginRef.current) {
         // Never left yet (still near drag-start) - nothing to protect
         // against, apply immediately like any other resolution.
         commitResolved(active, resolved);
         return;
      }
      // See the hysteresis comment above `originContainerRef` - a "back to
      // origin" resolution after having left is only trusted if it's still
      // the answer a short beat later, not applied on the first sighting.
      clearTimeout(returnToOriginDebounceRef.current);
      returnToOriginDebounceRef.current = setTimeout(() => commitResolved(active, resolved), 150);
   };

   const handleDragEnd = () => {
      clearTimeout(dragOverDebounceRef.current);
      clearTimeout(returnToOriginDebounceRef.current);
      if (!dragPreview || !dragPreview.activeEntry) { setDragPreview(null); return; }
      const { activeId, activeEntry } = dragPreview;
      const finalOver = latestOverRef.current || { containerId: dragPreview.overContainerId, index: dragPreview.overIndex };
      const finalPreview = { activeId, activeEntry, overContainerId: finalOver.containerId, overIndex: finalOver.index };

      const targetScope = allScopes.find((scope) => scope.containerId === finalPreview.overContainerId);
      if (!targetScope) { setDragPreview(null); return; }

      const finalOrder = applyDragPreview(targetScope.entries, finalPreview, finalPreview.overContainerId);
      const unchanged = finalOrder.length === targetScope.entries.length
         && finalOrder.every((entry, i) => entry.dndId === targetScope.entries[i].dndId);
      if (unchanged) { setDragPreview(null); return; }

      const saves = finalOrder.map((entry, index) => {
         const changes = { position: index };
         if (entry.dndId === activeId) changes.container = finalPreview.overContainerId;
         return saveEntry(entry, changes);
      });

      setAwaitingCommit(true);
      Promise.all(saves).then(() => reload()).catch((err) => {
         console.error(err);
         setAwaitingCommit(false);
         setDragPreview(null);
      });
   };

   const handleDragCancel = () => {
      clearTimeout(dragOverDebounceRef.current);
      clearTimeout(returnToOriginDebounceRef.current);
      setDragPreview(null);
   };

   const activeBlockType = dragPreview && dragPreview.activeEntry && dragPreview.activeEntry.kind === TOPIC_ENTRY_KIND_BLOCK
      ? getTopicBlockType(dragPreview.activeEntry.item.type)
      : null;
   const activeOverlayLabel = dragPreview && dragPreview.activeEntry
      ? (dragPreview.activeEntry.kind === TOPIC_ENTRY_KIND_BLOCK
         ? (activeBlockType ? activeBlockType.label : dragPreview.activeEntry.item.type)
         : dragPreview.activeEntry.kind === TOPIC_ENTRY_KIND_CONTAINER ? 'Контейнер' : 'Вправа')
      : null;

   return (
      <div className='topic-panel'>
         <h3 className='topic-panel-title'>Блоки контенту</h3>
         <DndContext sensors={sensors} collisionDetection={collisionDetectionRef.current}
            onDragStart={handleDragStart} onDragOver={handleDragOver} onDragEnd={handleDragEnd} onDragCancel={handleDragCancel}>
            <SortableContext items={renderedTopLevel.map((entry) => entry.dndId)} strategy={verticalListSortingStrategy}>
               <div className='exercise-block-list'>
                  {renderedTopLevel.map((entry, index) => (
                     entry.isDropPlaceholder ? (
                        <div key='drop-placeholder' className='exercise-block-drop-placeholder' />
                     ) : entry.kind === TOPIC_ENTRY_KIND_BLOCK ? (
                        <TopicElementCard key={'block-' + entry.item.id} topicId={topicId} courseId={courseId} block={entry.item}
                           canMoveUp={index > 0} canMoveDown={index < renderedTopLevel.length - 1}
                           onMove={(direction) => handleMove(index, direction)}
                           onDeleted={reload} onSaved={reload} />
                     ) : entry.kind === TOPIC_ENTRY_KIND_CONTAINER ? (
                        <TopicContainerCard key={'container-' + entry.item.id} topicId={topicId} courseId={courseId} container={entry.item}
                           allElements={elements} dragPreview={dragPreview}
                           canMoveUp={index > 0} canMoveDown={index < renderedTopLevel.length - 1}
                           onMove={(direction) => handleMove(index, direction)}
                           onDeleted={reload} onReload={reload} />
                     ) : (
                        <TopicExerciseCard key={'exercise-' + entry.item.id} courseId={courseId} link={entry.item}
                           canMoveUp={index > 0} canMoveDown={index < renderedTopLevel.length - 1}
                           onMove={(direction) => handleMove(index, direction)}
                           onDeleted={reload} />
                     )
                  ))}
                  <DroppableRegion id={TOP_LEVEL_END_ID} className='exercise-block-add-wrap' overClassName='exercise-block-dropzone-over'>
                     <div className='exercise-block-add-trigger' onClick={() => setPickerOpen((prev) => !prev)}>
                        <IconAdd />
                        <span>Додати блок, контейнер або вправу</span>
                     </div>
                     {pickerOpen ? (
                        <div className='exercise-block-type-picker'>
                           <div className='exercise-block-type-group-label'>Блок</div>
                           <div className='exercise-block-type-row'>
                              {TOPIC_BLOCK_TYPE_LIST.map((bt) => (
                                 <div key={bt.type} className='exercise-block-type-chip' style={{ '--accent': bt.accent }} onClick={() => handleAddBlock(bt)}>
                                    {bt.label}
                                 </div>
                              ))}
                           </div>
                           <div className='exercise-block-type-group-label'>Контейнер (розміщення)</div>
                           <div className='exercise-block-type-row'>
                              {CONTAINER_LAYOUT_TYPE_LIST.map((lt) => (
                                 <div key={lt.key} className='exercise-block-type-chip' style={{ '--accent': '#7ec2e8' }} onClick={() => handleAddContainer(lt)}>
                                    {lt.label}
                                 </div>
                              ))}
                           </div>
                           <div className='exercise-block-type-group-label'>Вправа</div>
                           <div className='topic-panel-add-row'>
                              <ExerciseTemplatePicker value={pickedExerciseId} onChange={setPickedExerciseId}
                                 excludeIds={exerciseLinks.map((l) => (l.exerciseTemplate ? l.exerciseTemplate.id : null))}
                                 placeholder='Пошук шаблону вправи...' />
                              <div className='topic-panel-add-btn' onClick={handleAttachExercise} title='Додати вправу'>
                                 <IconAdd />
                              </div>
                           </div>
                           <div className='topic-panel-create-row' onClick={() => setCreateExerciseOpen(true)}>
                              <IconAdd />
                              <span>Створити нову вправу</span>
                           </div>
                        </div>
                     ) : ''}
                  </DroppableRegion>
               </div>
            </SortableContext>
            <DragOverlay>
               {dragPreview && dragPreview.activeEntry ? (
                  <div className='exercise-block-card exercise-block-card-overlay'>
                     <div className='exercise-block-card-toolbar'>
                        <span className='exercise-block-card-type-label'>{activeOverlayLabel}</span>
                     </div>
                  </div>
               ) : null}
            </DragOverlay>
         </DndContext>
         <ExerciseTemplateCreateModal isOpen={createExerciseOpen} onClose={() => setCreateExerciseOpen(false)} onCreated={handleExerciseCreated} />
      </div>
   );
}
